// De EventResults-documenten van één ingevuld registratieformulier.
//
// Nodig omdat dezelfde opbouw op drie plekken stond of komt te staan: het
// beheerscherm 'Resultaat toevoegen' (AddEvent.jsx), het formulier via de link
// (EventOpen.jsx, sinds 2026-10-15 via de Cloud Function saveEventRegistration)
// en die Cloud Function zelf. De twee schermen hadden elk een eigen kopie; de
// server heeft een spiegel met een pariteitstest
// (functions/events/buildRegistrationDocs.parity.test.js).
// AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
//
// Puur: de aanroeper bepaalt het tijdstip (een Firestore-Timestamp is in de
// browser en op de server een ander type), de sessie en wie het invulde.

import { buildDateResults } from './dateFieldResults.js'

/**
 * @typedef {object} OutputEntry
 * @property {string} fieldId - Het outputs-veld van het formulier
 * @property {string} outputId - De output
 * @property {string|number} value - Het ingevulde aantal; leeg telt als 0
 * @property {Array<string>} [participantIds] - Aangevinkte deelnemers
 */

/**
 * @typedef {object} ResponseEntry
 * @property {string} type - Veldsoort ('scale', 'number', 'multiple-one', 'multiple-multiple', 'open')
 * @property {Array<{value: *, optionId?: string}>} values - Eén of meer antwoorden
 */

/**
 * @typedef {object} RegistrationBuffers
 * @property {Array<OutputEntry>} [outputsArray]
 * @property {Object<string, ResponseEntry>} [responses] - Schaal, getal en meerkeuze per veld
 * @property {Object<string, ResponseEntry>} [openResponses] - Open antwoorden per veld
 * @property {Object<string, string>} [datesByFieldId] - 'yyyy-mm-dd' per datumveld
 * @property {Object<string, Array<string>>} [participantsByFieldId] - Deelnemer-ID's per veld
 * @property {Object<string, Array<string>>} [targetgroupsByFieldId] - Doelgroep-ID's per veld
 * @property {Object<string, Object<string, Array<string>>>} [subTargetgroupsByFieldId] - Per veld, per doelgroep de subdoelgroepen
 */

/**
 * @typedef {object} RegistrationMeta
 * @property {string} client - CompagnyID
 * @property {string} activityId - De activiteit van het formulier
 * @property {string} eventId - Het formulier (EventForms.ID)
 * @property {string} sessionId - Bindt alle documenten van deze invulling
 * @property {string|null} user - Wie het invulde; null bij een anonieme invuller
 * @property {*} timestamp - Firestore-Timestamp van het moment van de registratie
 */

/**
 * @param {object} input
 * @param {RegistrationBuffers} input.buffers - Wat er is ingevuld
 * @param {RegistrationMeta} input.meta - Gedeelde velden
 * @param {() => string} input.newId - Levert per document een nieuw ID
 * @returns {Array<object>} De te schrijven documenten
 */
const buildRegistrationDocs = ({ buffers, meta, newId }) => {
    const b = buffers || {}
    const { client, activityId, eventId, sessionId, user, timestamp } = meta

    const outputDocs = (b.outputsArray || []).map((output) => ({
        Compagny: client,
        CompagnyID: client,
        ID: newId(),
        Value: Number(output.value || 0),
        Timestamp: timestamp,
        OutputID: output.outputId,
        FieldID: output.fieldId,
        ActivityID: activityId,
        EventID: eventId,
        SessionID: sessionId,
        User: user,
        Type: 'outputs',
        Participants: output.participantIds || [],
    }))

    const baseMeta = {
        CompagnyID: client,
        Compagny: client,
        Timestamp: timestamp,
        EventID: eventId,
        SessionID: sessionId,
        User: user,
    }

    const fromEntries = (map, typeOf) => Object.entries(map || {}).flatMap(([fieldId, entry]) =>
        (entry?.values || []).map((v) => ({
            ...baseMeta,
            FieldID: fieldId,
            Value: v.value,
            OptionID: v.optionId || null,
            Type: typeOf(entry),
            ID: newId(),
        })))

    const fromIdLists = (map, type) => Object.entries(map || {}).flatMap(([fieldId, list]) =>
        (list || []).map((value) => ({
            ...baseMeta,
            FieldID: fieldId,
            Value: value,
            OptionID: null,
            Type: type,
            ID: newId(),
        })))

    const responseDocs = fromEntries(b.responses, (entry) => entry.type)
    const openDocs = fromEntries(b.openResponses, () => 'open')
    const dateDocs = buildDateResults(b.datesByFieldId, baseMeta).map((doc) => ({ ...doc, ID: newId() }))
    const participantDocs = fromIdLists(b.participantsByFieldId, 'participants')
    const targetgroupDocs = fromIdLists(b.targetgroupsByFieldId, 'targetgroup')
    const subTargetgroupDocs = Object.entries(b.subTargetgroupsByFieldId || {}).flatMap(([fieldId, tgMap]) =>
        Object.entries(tgMap || {}).flatMap(([tgId, subList]) =>
            (subList || []).map((subId) => ({
                ...baseMeta,
                FieldID: fieldId,
                Value: subId,
                OptionID: null,
                Type: 'subtargetgroup',
                ParentTargetgroupID: tgId,
                ID: newId(),
            }))))

    return [
        ...outputDocs,
        ...responseDocs,
        ...openDocs,
        ...dateDocs,
        ...participantDocs,
        ...targetgroupDocs,
        ...subTargetgroupDocs,
    ]
}

export default buildRegistrationDocs
